import { Modal, Input, Table, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';
import { getChannelList } from '@/services/ant-design-pro/api';

export interface SelectChannelModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: (record: API.ChannelItem) => void;
  title?: string;
  width?: number;
  height?: number;
}

const SelectChannelModal: React.FC<SelectChannelModalProps> = ({
  open,
  onCancel,
  onOk,
  title = '选择广告',
  width = 350,
  height = 360,
}) => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<API.ChannelItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<API.ChannelItem | null>(null);
  const tableScrollY = Math.max(height - 80, 200);

  const columns: ColumnsType<API.ChannelItem> = useMemo(
    () => [
      {
        title: '渠道名称',
        dataIndex: 'channelName',
        key: 'channelId',
      }
    ],
    [],
  );

  const fetchData = async (channelname?: string) => {
    setLoading(true);
    try {
      const response = await getChannelList({
        pageSize: 350,
        channelName: channelname,
      });
      if (response.code === 0 && response.data?.list) {
        setDataSource(response.data.list);
      } else {
        setDataSource([]);
      }
    } catch (_error) {
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedRowKeys([]);
      setSelectedRecord(null);
      fetchData();
    }
  }, [open]);

  const handleSearch = () => {
    fetchData(searchKeyword || undefined);
  };

  const handleOk = () => {
    if (selectedRecord) {
      onOk(selectedRecord);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      width={width}
      onCancel={onCancel}
      zIndex={2000}
      styles={{ body: { height, display: 'flex', flexDirection: 'column', padding: 0 } }}
      footer={[
        <Button
          key="ok"
          type="primary"
          onClick={handleOk}
          disabled={!selectedRecord}
        >
          确定
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
          <Input
            placeholder="请输入广告名称"
            allowClear
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
          />
        </div>
        <div style={{ flex: 1 }}>
          <Table<API.ChannelItem>
            rowKey={(record) => String(record.channelId || '')}
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={false}
            scroll={{ y: tableScrollY }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys,
              onChange: (keys, rows) => {
                setSelectedRowKeys(keys);
                setSelectedRecord(rows[0] || null);
              },
            }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRowKeys([String(record.channelId || '')]);
                setSelectedRecord(record);
              },
            })}
            size="small"
          />
        </div>
      </div>
    </Modal>
  );
};

export default SelectChannelModal;
